import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { API_CONFIG } from '../config/api';

// Note: planUtils would need to be implemented on frontend or we need a backend endpoint
// For now, we'll implement the transformation logic directly

interface SubscriptionPlanData {
  name: string;
  description: string;
  duration: number;
  price: number;
  maxChatsPerMonth: number;
  features: string[];
  access: string[];
}

type ValidatableField = 'name' | 'description' | 'duration' | 'price' | 'maxChatsPerMonth';

interface ValidationErrors {
  name?: string;
  description?: string;
  duration?: string;
  price?: string;
  maxChatsPerMonth?: string;
}

interface UserNavItem {
  to: string;
  label: string;
}

// User panel navigation items that can be controlled by subscription plans
const USER_NAV_ITEMS: UserNavItem[] = [
  { to: 'dashboard', label: 'Dashboard' },
  { to: 'chat', label: 'Chat' },
  { to: 'wellness', label: 'Wellness' },
  { to: 'wellness-loop', label: 'Wellness Loop' },
  { to: 'learn', label: 'Learn' },
  { to: 'insights', label: 'Insights' },
  { to: 'consultations', label: 'Consultations' },
  { to: 'group-list', label: 'Group List' },
];

// DASS-21 assessment filter for user panel access
const ASSESSMENT_FILTERS = [
  { key: 'none', label: 'No Assessment Filter' },
  { key: 'dass21', label: 'DASS-21 Assessment Only' },
  { key: 'spiritual', label: 'Spiritual Assessment Only' },
  { key: 'nutritional', label: 'Nutritional Assessment Only' },
  { key: 'physical', label: 'Physical Assessment Only' },
  { key: 'financial', label: 'Financial Assessment Only' },
];

// Resource access filters
const RESOURCE_FILTERS = [
  { key: 'all', label: 'All Resources Available' },
  { key: 'none', label: 'No Resources Available' },
];

const SubscriptionPlanEditPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<SubscriptionPlanData>({
    name: '',
    description: '',
    duration: 30,
    price: 0,
    maxChatsPerMonth: 100,
    features: ['Unlimited Access', 'Email Support', 'Exclusive Content'],
    access: ['dashboard']
  });

  const [newFeature, setNewFeature] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentFilter, setAssessmentFilter] = useState<string>('none');
  const [resourceFilter, setResourceFilter] = useState<string>('all');
  const [showAssessmentDropdown, setShowAssessmentDropdown] = useState(false);
  const [showResourceDropdown, setShowResourceDropdown] = useState(false);

  // Fetch plan data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchPlanData = async () => {
        try {
          setIsFetching(true);
          const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_PLANS}/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch plan data');
          }

          const result = await response.json();

          if (!result.success) {
            throw new Error(result.message || 'Failed to fetch plan data');
          }

          const planData = result.data;
          console.log('Fetched plan data:', planData);
          setFormData({
            name: planData.name || '',
            description: planData.description || '',
            duration: planData.duration || 30,
            price: planData.price || 0,
            maxChatsPerMonth: typeof planData.maxChatsPerMonth === 'number' ? planData.maxChatsPerMonth : 100,
            features: planData.features || [],
            access: Array.isArray(planData.access) ? planData.access : ['dashboard']
          });
          setAssessmentFilter(planData.assessmentFilter || 'none');
          setResourceFilter(planData.resourceFilter || 'all');
          console.log('Setting resourceFilter to:', planData.resourceFilter || 'all');
        } catch (err) {

          setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
          setIsFetching(false);
        }
      };

      fetchPlanData();
    }
  }, [id, isEditMode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.subscription-plan-edit__filter-dropdown')) {
        setShowAssessmentDropdown(false);
        setShowResourceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (field: keyof SubscriptionPlanData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error when user starts typing (only for validatable fields)
    const validatableField = field as ValidatableField;
    if (validationErrors[validatableField]) {
      setValidationErrors(prev => ({
        ...prev,
        [validatableField]: undefined
      }));
    }
  };

  const handleAddFeature = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newFeature.trim()) {
      e.preventDefault();
      if (!formData.features.includes(newFeature.trim())) {
        setFormData(prev => ({
          ...prev,
          features: [...prev.features, newFeature.trim()]
        }));
      }
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (featureToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(feature => feature !== featureToRemove)
    }));
  };

  const handleAccessChange = (navItem: string, checked: boolean) => {
    setFormData(prev => {
      const currentAccess = Array.isArray(prev.access) ? prev.access : ['dashboard'];
      return {
        ...prev,
        access: checked
          ? [...currentAccess, navItem]
          : currentAccess.filter(item => item !== navItem)
      };
    });
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'Plan name is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (formData.duration <= 0) {
      errors.duration = 'Duration must be greater than 0';
    }

    if (formData.price < 0) {
      errors.price = 'Price cannot be negative';
    }

    if (formData.maxChatsPerMonth < 0) {
      errors.maxChatsPerMonth = 'Max chats per month cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    console.log('handleSave called');
    console.log('isEditMode:', isEditMode);
    console.log('id:', id);
    console.log('resourceFilter state:', resourceFilter);
    console.log('Plan name:', formData.name);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed');

    try {
      setIsLoading(true);
      setError(null);

      // Attach assessmentFilter as a top-level property for backend mapping
      const planDataToSave = {
        ...formData,
        assessmentFilter,
        resourceFilter,
      };
      console.log('Saving plan data:', planDataToSave);

      const url = isEditMode
        ? `${API_CONFIG.ENDPOINTS.BILLING_PLANS}/${id}`
        : API_CONFIG.ENDPOINTS.BILLING_PLANS;

      const method = isEditMode ? 'PUT' : 'POST';

      console.log('About to make fetch request to:', url);
      console.log('Method:', method);
      console.log('Sending data:', JSON.stringify(planDataToSave, null, 2));

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(planDataToSave)
      });

      console.log('Save response status:', response.status);
      console.log('Save response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Save error response:', errorData);
        throw new Error(errorData.message || `Failed to ${isEditMode ? 'update' : 'create'} plan`);
      }

      const result = await response.json();
      console.log('Save success result:', result);

      // Show success notification
      toast.success(`Subscription plan ${isEditMode ? 'updated' : 'created'} successfully!`);

      // Navigate back to subscription plans page
      navigate('/panel/admin/subscription-plans');
    } catch (err) {

      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} subscription plan: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !id) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_PLANS}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete plan');
      }

      // Close modal and navigate back to subscription plans page
      setShowDeleteModal(false);
      navigate('/panel/admin/subscription-plans');
    } catch (err) {

      setError(err instanceof Error ? err.message : 'An error occurred');
      setShowDeleteModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="subscription-plan-edit">
        <div className="subscription-plan-edit__container">
          <div className="subscription-plan-edit__loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-plan-edit">
      <div className="subscription-plan-edit__container">
        <div className="subscription-plan-edit__header">
          <div className="subscription-plan-edit__title-section">
            <h1 className="subscription-plan-edit__title">
              {isEditMode ? 'Edit Subscription Plan' : 'Create a New Subscription Plan'}
            </h1>
            <p className="subscription-plan-edit__subtitle">
              {isEditMode
                ? 'Update the details below to modify this subscription plan.'
                : 'Fill in the details below to set up a new plan for your subscribers.'
              }
            </p>
          </div>
        </div>

        <div className="subscription-plan-edit__form">
          <div className="subscription-plan-edit__form-grid">
            {/* Plan Name */}
            <label className="subscription-plan-edit__field">
              <p className="subscription-plan-edit__field-label">
                Plan Name
                {['Free', 'Basic', 'Premium', 'Enterprise'].includes(formData.name) && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>(System Plan - Read Only)</span>
                )}
              </p>
              <input
                className={`subscription-plan-edit__input ${validationErrors.name ? 'subscription-plan-edit__input--error' : ''}`}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={['Free', 'Basic', 'Premium', 'Enterprise'].includes(formData.name)}
                title={['Free', 'Basic', 'Premium', 'Enterprise'].includes(formData.name) ? 'System plan names cannot be edited' : ''}
              />
              {validationErrors.name && (
                <span className="subscription-plan-edit__error">{validationErrors.name}</span>
              )}
            </label>

            {/* Description */}
            <label className="subscription-plan-edit__field subscription-plan-edit__field--full">
              <p className="subscription-plan-edit__field-label">Description</p>
              <textarea
                className={`subscription-plan-edit__textarea ${validationErrors.description ? 'subscription-plan-edit__textarea--error' : ''}`}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
              />
              {validationErrors.description && (
                <span className="subscription-plan-edit__error">{validationErrors.description}</span>
              )}
            </label>

            {/* Duration, Price, Max Chats */}
            <div className="subscription-plan-edit__field-group">
              <label className="subscription-plan-edit__field">
                <p className="subscription-plan-edit__field-label">Duration (days)</p>
                <input
                  className={`subscription-plan-edit__input ${validationErrors.duration ? 'subscription-plan-edit__input--error' : ''}`}
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', Number(e.target.value))}
                  min="1"
                />
                {validationErrors.duration && (
                  <span className="subscription-plan-edit__error">{validationErrors.duration}</span>
                )}
              </label>

              <label className="subscription-plan-edit__field">
                <p className="subscription-plan-edit__field-label">Price (₱)</p>
                <input
                  className={`subscription-plan-edit__input ${validationErrors.price ? 'subscription-plan-edit__input--error' : ''}`}
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', Number(e.target.value))}
                  min="0"
                  step="0.01"
                />
                {validationErrors.price && (
                  <span className="subscription-plan-edit__error">{validationErrors.price}</span>
                )}
              </label>

              <label className="subscription-plan-edit__field">
                <p className="subscription-plan-edit__field-label">Max Chats per Month</p>
                <input
                  className={`subscription-plan-edit__input ${validationErrors.maxChatsPerMonth ? 'subscription-plan-edit__input--error' : ''}`}
                  type="number"
                  value={formData.maxChatsPerMonth}
                  onChange={(e) => handleInputChange('maxChatsPerMonth', Number(e.target.value))}
                  min="1"
                />
                {validationErrors.maxChatsPerMonth && (
                  <span className="subscription-plan-edit__error">{validationErrors.maxChatsPerMonth}</span>
                )}
              </label>
            </div>

            {/* Plan Features */}
            <div className="subscription-plan-edit__field subscription-plan-edit__field--full">
              <p className="subscription-plan-edit__field-label">Plan Features (optional)</p>
              <div className="subscription-plan-edit__features-container">
                {formData.features.map((feature, index) => (
                  <div key={index} className="subscription-plan-edit__feature-tag">
                    <span className="subscription-plan-edit__feature-text">{feature}</span>
                    <button
                      type="button"
                      className="subscription-plan-edit__feature-remove"
                      onClick={() => handleRemoveFeature(feature)}
                      aria-label={`Remove ${feature}`}
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>
                ))}
                <input
                  className="subscription-plan-edit__feature-input"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={handleAddFeature}
                />
              </div>
            </div>

            {/* User Panel Access */}
            <div className="subscription-plan-edit__field subscription-plan-edit__field--full">
              <p className="subscription-plan-edit__field-label">User Panel Access</p>
              <p className="subscription-plan-edit__field-description">
                Select which pages users on this plan can access in their panel sidebar.
              </p>
              <div className="subscription-plan-edit__access-container">
                {USER_NAV_ITEMS.map((navItem) => (
                  <label key={navItem.to} className="subscription-plan-edit__access-item">
                    <input
                      type="checkbox"
                      checked={Array.isArray(formData.access) && formData.access.includes(navItem.to)}
                      onChange={(e) => handleAccessChange(navItem.to, e.target.checked)}
                      className="subscription-plan-edit__access-checkbox"
                    />
                    <span className="subscription-plan-edit__access-label">{navItem.label}</span>
                  </label>
                ))}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label className="subscription-plan-edit__field-label">Assessment Filter</label>
                <div className="subscription-plan-edit__filter-dropdown">
                  <button
                    className={`subscription-plan-edit__filter-btn ${assessmentFilter !== 'none' ? 'subscription-plan-edit__filter-btn--active' : ''}`}
                    onClick={() => setShowAssessmentDropdown(!showAssessmentDropdown)}
                    type="button"
                  >
                    <div className="subscription-plan-edit__filter-content">
                      <span className="material-symbols-outlined">tune</span>
                      <p>{ASSESSMENT_FILTERS.find(f => f.key === assessmentFilter)?.label || 'No Assessment Filter'}</p>
                    </div>
                    <span className="material-symbols-outlined subscription-plan-edit__arrow">expand_more</span>
                  </button>
                  {showAssessmentDropdown && (
                    <div className="subscription-plan-edit__dropdown-menu" onClick={() => setShowAssessmentDropdown(false)}>
                      {ASSESSMENT_FILTERS.map(f => (
                        <button
                          key={f.key}
                          className="subscription-plan-edit__dropdown-item"
                          onClick={() => { setAssessmentFilter(f.key); setShowAssessmentDropdown(false); }}
                          type="button"
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="subscription-plan-edit__field-description">
                  Restrict users to specific assessments. If set to an assessment type, users will only be able to access that specific assessment in the Learn page. All other assessments will be locked.
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label className="subscription-plan-edit__field-label">Resource Filter</label>
                <div className="subscription-plan-edit__filter-dropdown">
                  <button
                    className={`subscription-plan-edit__filter-btn ${resourceFilter !== 'all' ? 'subscription-plan-edit__filter-btn--active' : ''}`}
                    onClick={() => setShowResourceDropdown(!showResourceDropdown)}
                    type="button"
                  >
                    <div className="subscription-plan-edit__filter-content">
                      <span className="material-symbols-outlined">library_books</span>
                      <p>{RESOURCE_FILTERS.find(f => f.key === resourceFilter)?.label || 'All Resources Available'}</p>
                    </div>
                    <span className="material-symbols-outlined subscription-plan-edit__arrow">expand_more</span>
                  </button>
                  {showResourceDropdown && (
                    <div className="subscription-plan-edit__dropdown-menu" onClick={() => setShowResourceDropdown(false)}>
                      {RESOURCE_FILTERS.map(f => (
                        <button
                          key={f.key}
                          className="subscription-plan-edit__dropdown-item"
                          onClick={() => { setResourceFilter(f.key); setShowResourceDropdown(false); }}
                          type="button"
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="subscription-plan-edit__field-description">
                  Control access to learning resources. "All Resources Available" allows access to all resource pages. "No Resources Available" locks all resource buttons.
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="subscription-plan-edit__error-message">
              {error}
            </div>
          )}

          <hr className="subscription-plan-edit__divider" />

          <div className="subscription-plan-edit__actions">
            {isEditMode && !['Free', 'Basic', 'Premium', 'Enterprise'].includes(formData.name) && (
              <Button
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
                disabled={isLoading}
                className="subscription-plan-edit__delete-btn"
              >
                <span>Delete</span>
              </Button>
            )}
            <div className="subscription-plan-edit__actions-spacer" />
            <Button
              variant="secondary"
              onClick={() => navigate('/panel/admin/subscription-plans')}
              disabled={isLoading}
              className="subscription-plan-edit__cancel-btn"
            >
              <span>Cancel</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isLoading}
              className="subscription-plan-edit__save-btn"
            >
              <span>{isLoading ? 'Saving...' : 'Save'}</span>
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showDeleteModal}
        title="Delete Subscription Plan"
        onClose={() => setShowDeleteModal(false)}
        actionLabel={isLoading ? "Deleting..." : "Delete Plan"}
        onAction={handleDelete}
        actionDisabled={isLoading}
      >
        <div className="subscription-plan-edit__modal-content">
          <div className="subscription-plan-edit__modal-content__warning-icon">⚠️</div>
          <p className="subscription-plan-edit__modal-content__title">
            Are you sure you want to permanently delete this subscription plan?
          </p>
          <p className="subscription-plan-edit__modal-content__message">
            This action cannot be undone. The plan will be permanently removed from the system and cannot be restored.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionPlanEditPage;

